import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  generatePersonalApiKey,
  getApiKeyStatus,
} from '@kirin/api-client/api/account';
import {
  expirePluginApiKey,
  generateOrganizationApiKey,
  getOrganizationApiKeyStatus,
  generateOrganizationLevelApiKey,
  getOrganizationLevelApiKeyStatus,
  listPluginApiKeys,
} from '@kirin/api-client/api/organizations';
import { apiClient } from '@/lib/api-client';
import { trackEvent } from '@/lib/analytics';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';

/**
 * Variables for API key generation mutations
 * Used to track whether this is a create or regenerate action
 */
export interface ApiKeyGenerationVariables {
  hadKeyBefore: boolean;
}

/**
 * Shared configuration for API key operations
 *
 * Centralizes error handling logic for consistent behavior across all API key queries.
 * Cache config uses QueryClient defaults (user-specific data).
 */
const API_KEY_QUERY_CONFIG = {
  retry: retryHandlers.list,
} as const;

/**
 * Hook to fetch personal API key status
 *
 * Uses React Query for caching and automatic refetching.
 * API key status is cached for 5 minutes.
 *
 * @example
 * const { data: status, isLoading, error } = usePersonalApiKeyStatus();
 *
 * @returns React Query result with API key status
 */
export interface UsePersonalApiKeyStatusOptions {
  enabled?: boolean;
}
export function usePersonalApiKeyStatus({
  enabled = true,
}: UsePersonalApiKeyStatusOptions = {}) {
  return useQuery({
    queryKey: queryKeys.account.apiKeyStatus,
    queryFn: () => getApiKeyStatus(apiClient),
    enabled,
    ...API_KEY_QUERY_CONFIG,
  });
}

/**
 * Hook to generate a new personal API key
 *
 * @example
 * const generateKey = useGeneratePersonalApiKey();
 * const handleGenerate = () => generateKey.mutate({ hadKeyBefore: false });
 *
 * @returns React Query mutation for generating personal API key
 */
export function useGeneratePersonalApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (_variables: ApiKeyGenerationVariables) =>
      generatePersonalApiKey(apiClient),
    onSuccess: (_data, variables) => {
      // Track API key generation event with create/regenerate distinction
      trackEvent('api_key_generated', {
        key_type: 'personal',
        action: variables.hadKeyBefore ? 'regenerate' : 'create',
      });

      // Invalidate and refetch personal API key status
      queryClient.invalidateQueries({
        queryKey: queryKeys.account.apiKeyStatus,
      });
    },
  });
}

/**
 * Hook to fetch organization API key status
 *
 * Uses React Query for caching and automatic refetching.
 * API key status is cached for 5 minutes.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data: status, isLoading, error } = useOrganizationApiKeyStatus(orgId);
 *
 * @returns React Query result with API key status
 */
export function useOrganizationApiKeyStatus(
  organizationId: string,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.organizations.apiKeyStatus(organizationId),
    queryFn: () => getOrganizationApiKeyStatus(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    ...API_KEY_QUERY_CONFIG,
  });
}

/**
 * Hook to generate a new organization API key
 *
 * @param organizationId - The organization ID
 *
 * @example
 * const generateKey = useGenerateOrganizationApiKey(orgId);
 * const handleGenerate = () => generateKey.mutate({ hadKeyBefore: false });
 *
 * @returns React Query mutation for generating organization API key
 */
export function useGenerateOrganizationApiKey(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (_variables: ApiKeyGenerationVariables) =>
      generateOrganizationApiKey(apiClient, organizationId),
    onSuccess: (_data, variables) => {
      // Track API key generation event with create/regenerate distinction
      trackEvent('api_key_generated', {
        key_type: 'org_member',
        organization_id: organizationId,
        action: variables.hadKeyBefore ? 'regenerate' : 'create',
      });

      // Invalidate and refetch organization API key status
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.apiKeyStatus(organizationId),
      });
    },
  });
}

/**
 * Hook to fetch organization-level API key status (for MDM installation)
 * Only owners can view this - shows key prefix to all owners
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data: status, isLoading, error } = useOrganizationLevelApiKeyStatus(orgId);
 *
 * @returns React Query result with organization-level API key status
 */
export function useOrganizationLevelApiKeyStatus(
  organizationId: string,
  enabled = true
) {
  return useQuery({
    queryKey:
      queryKeys.organizations.organizationLevelApiKeyStatus(organizationId),
    queryFn: () => getOrganizationLevelApiKeyStatus(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    ...API_KEY_QUERY_CONFIG,
  });
}

/**
 * Hook to generate a new organization-level API key (for MDM installation)
 * Only owners can generate organization-level keys
 *
 * @param organizationId - The organization ID
 *
 * @example
 * const generateKey = useGenerateOrganizationLevelApiKey(orgId);
 * const handleGenerate = () => generateKey.mutate({ hadKeyBefore: false });
 *
 * @returns React Query mutation for generating organization-level API key
 */
export function useGenerateOrganizationLevelApiKey(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (_variables: ApiKeyGenerationVariables) =>
      generateOrganizationLevelApiKey(apiClient, organizationId),
    onSuccess: (_data, variables) => {
      // Track API key generation event with create/regenerate distinction
      trackEvent('api_key_generated', {
        key_type: 'org_global',
        organization_id: organizationId,
        action: variables.hadKeyBefore ? 'regenerate' : 'create',
      });

      // Invalidate and refetch organization-level API key status
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.organizations.organizationLevelApiKeyStatus(organizationId),
      });
    },
  });
}

/**
 * Hook to list every plugin API key (Claude Desktop hook plugin) for an org.
 * Includes active and expired keys.
 */
export function usePluginApiKeys(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizations.pluginApiKeys(organizationId),
    queryFn: () => listPluginApiKeys(apiClient, organizationId),
    enabled: enabled && !!organizationId,
    ...API_KEY_QUERY_CONFIG,
  });
}

/**
 * Hook to manually expire a plugin API key. Invalidates the list on success.
 */
export function useExpirePluginApiKey(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      expirePluginApiKey(apiClient, organizationId, keyId),
    onSuccess: () => {
      trackEvent('plugin_api_key_expired', { organization_id: organizationId });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.pluginApiKeys(organizationId),
      });
    },
  });
}
